import React, { useState, useEffect } from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Typography,
  Collapse,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from '@material-ui/core'
import { Edit, Delete, Add, KeyboardArrowDown, KeyboardArrowUp } from '@material-ui/icons'
import { makeStyles } from '@material-ui/core/styles'
import { useMutation, useQueryClient } from 'react-query'
import gamedaysTabStyles from './gamedaysTab.styles'
import EditGamedayModal from './EditGamedayModal/EditGamedayModal'
import BulkAddGamedaysModal from './BulkAddGamedaysModal/BulkAddGamedaysModal'
import deleteGameday from '../../../Apis/gamedays/deleteGameday'
import QueryKeys from '../../../utils/constants/queryKeys'
import { parseDate } from '../../../utils/helpers/dateHelpers/parseDate'
import LiteModeBadge from '../../../components/LiteModeBadge/LiteModeBadge'

const useStyles = makeStyles(gamedaysTabStyles)

const GamedaysTab = ({ orgId, seasonId, selectedStage, selectedGroup, isLoading, onGamedayClick }) => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const [openModal, setOpenModal] = useState(false)
  const [openBulkModal, setOpenBulkModal] = useState(false)
  const [selectedGameday, setSelectedGameday] = useState(null)
  const [expandedGamedays, setExpandedGamedays] = useState({})
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [gamedayToDelete, setGamedayToDelete] = useState(null)

  // Get gamedays from the selected group
  const gamedays = selectedGroup?.gamedays || []

  const deleteGamedayMutation = useMutation(
    (gamedayId) => deleteGameday({ orgId, gamedayId }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([QueryKeys.GET_STAGES, orgId, seasonId])
        setDeleteConfirmOpen(false)
        setGamedayToDelete(null)
      },
    }
  )

  const handleGamedayClick = (gameday) => {
    if (onGamedayClick) {
      onGamedayClick(gameday)
    }
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setSelectedGameday(null)
  }

  const handleEditGameday = (gamedayId) => {
    const gameday = gamedays.find((gd) => gd.id === gamedayId)
    if (gameday) {
      setSelectedGameday(gameday)
      setOpenModal(true)
    }
  }

  const handleDeleteGameday = (gameday) => {
    setGamedayToDelete(gameday)
    setDeleteConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (gamedayToDelete) {
      deleteGamedayMutation.mutate(gamedayToDelete.id)
    }
  }

  const toggleGamedayExpansion = (gamedayId) => {
    setExpandedGamedays((prev) => ({
      ...prev,
      [gamedayId]: !prev[gamedayId],
    }))
  }

  // Initialize all gamedays as expanded when gamedays data is loaded
  useEffect(() => {
    if (gamedays && gamedays.length > 0) {
      const initialExpandedState = {}
      gamedays.forEach((gameday) => {
        initialExpandedState[gameday.id] = true
      })
      setExpandedGamedays(initialExpandedState)
    }
  }, [gamedays])

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        {selectedGroup ? (
          <div className={classes.headerActions}>
            <Button
              variant='contained'
              color='primary'
              startIcon={<Add />}
              onClick={() => setOpenBulkModal(true)}
            >
              Add gamedays
            </Button>
          </div>
        ) : (
          <Typography variant='body2' color='textSecondary'>
            Please select a group to manage gamedays
          </Typography>
        )}
      </div>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell style={{paddingTop: 10, paddingBottom: 10, width: 50}} />
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>Gameday Name</TableCell>
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>Start Date</TableCell>
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>End Date</TableCell>
              <TableCell style={{paddingTop: 10, paddingBottom: 10}}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align='center' style={{ padding: 32 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : gamedays.length > 0 ? (
              gamedays.map((gameday) => {
                const isExpanded = expandedGamedays[gameday.id]
                const games = gameday.games || []
                const hasGames = games.length > 0

                return (
                  <React.Fragment key={gameday.id}>
                    {/* Gameday Row */}
                    <TableRow hover className={classes.tableRow}>
                      <TableCell>
                        <IconButton
                          size='small'
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleGamedayExpansion(gameday.id)
                          }}
                          disabled={!hasGames}
                          className={classes.expandButton}
                        >
                          {hasGames && (isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />)}
                        </IconButton>
                      </TableCell>
                      <TableCell onClick={() => handleGamedayClick(gameday)}>
                        {gameday.name}
                        {hasGames && (
                          <Chip
                            label={`${games.length} ${games.length === 1 ? 'game' : 'games'}`}
                            size='small'
                            className={classes.gameChip}
                            color='primary'
                            variant='outlined'
                          />
                        )}
                      </TableCell>
                      <TableCell onClick={() => handleGamedayClick(gameday)}>
                        {parseDate(gameday.startDate) || 'N/A'}
                      </TableCell>
                      <TableCell onClick={() => handleGamedayClick(gameday)}>
                        {parseDate(gameday.endDate) || 'N/A'}
                      </TableCell>
                      <TableCell align='right'>
                        <div className={classes.actionButtons}>
                          <IconButton
                            size='small'
                            color='primary'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleEditGameday(gameday.id)
                            }}
                          >
                            <Edit />
                          </IconButton>
                          <IconButton
                            size='small'
                            color='secondary'
                            onClick={(e) => {
                              e.stopPropagation()
                              handleDeleteGameday(gameday)
                            }}
                          >
                            <Delete />
                          </IconButton>
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Games Row (Collapsible) */}
                    {hasGames && (
                      <TableRow>
                        <TableCell className={classes.gamesCell} colSpan={5}>
                          <Collapse in={isExpanded} timeout='auto' unmountOnExit>
                            <Box className={classes.gamesContainer}>
                              <Typography variant='subtitle2' gutterBottom>
                                Games in this gameday:
                              </Typography>
                              {games.map((game) => (
                                <div key={game.id} className={classes.gameItem}>
                                  <Typography variant='body2'>
                                    • {game.title || 'TBD'}
                                  </Typography>
                                  <LiteModeBadge statsMode={game.statsMode} />
                                  <Chip
                                    label={parseDate(game.startDateTime) || 'No date'}
                                    size='small'
                                    className={classes.gameChip}
                                  />
                                </div>
                              ))}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    )}
                  </React.Fragment>
                )
              })
            ) : (
              <TableRow>
                <TableCell colSpan={5} align='center'>
                  No gamedays found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {selectedGameday && selectedGroup && (
        <EditGamedayModal
          isOpen={openModal}
          onClose={handleCloseModal}
          orgId={orgId}
          seasonId={seasonId}
          groupId={selectedGroup.id}
          gameday={selectedGameday}
        />
      )}

      {/* Bulk Add Gamedays Modal */}
      {selectedGroup && (
        <BulkAddGamedaysModal
          isOpen={openBulkModal}
          onClose={() => setOpenBulkModal(false)}
          orgId={orgId}
          seasonId={seasonId}
          groupId={selectedGroup.id}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete gameday &quot;{gamedayToDelete?.name}&quot;?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color='secondary'
            variant='contained'
            disabled={deleteGamedayMutation.isLoading}
          >
            {deleteGamedayMutation.isLoading ? (
              <CircularProgress size={24} />
            ) : (
              'Delete'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default GamedaysTab
